import React, { useEffect, useRef, useState } from 'react';
import {
  FaBell,
  FaClock,
  FaTrash,
  FaPaperPlane,
  FaTimes,
  FaPlus,
  FaEdit,
  FaBolt,
  FaPaperclip,
  FaFile,
  FaChevronDown,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const EMAIL_PATTERN = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
const MAX_REMINDER_FILES = 10;
const MAX_PENDING_REMINDERS = 5;

const defaultFormState = {
  message: '',
  emailSubject: 'Reminder from BooksComply',
  scheduledAt: '',
  reminderType: 'manual',
  repeatType: 'none',
  sendEmail: true,
  postToChat: true,
  ccEmails: [],
};

const toDatetimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoFromDatetimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const getWaitLabel = (scheduledAt) => {
  const target = new Date(scheduledAt).getTime();
  const diff = target - Date.now();
  if (Number.isNaN(target)) return '';
  if (diff <= 0) return 'Due now — sending shortly';
  const mins = Math.ceil(diff / 60000);
  if (mins < 60) return `Sends in ~${mins} min`;
  const hours = Math.ceil(mins / 60);
  if (hours < 48) return `Sends in ~${hours} hr`;
  const days = Math.ceil(hours / 24);
  return `Sends in ~${days} day${days > 1 ? 's' : ''}`;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const ChatReminderPanel = ({ userId, tokenKey = 'adminToken', defaultMessage = '', onScheduled }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'form'
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [ccInput, setCcInput] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultFormState);
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const fileInputRef = useRef(null);

  const loadReminders = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/reminders?userId=${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReminders(data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (userId) loadReminders();
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const timer = setInterval(loadReminders, 30000);
    return () => clearInterval(timer);
  }, [userId]);

  useEffect(() => {
    if (open && !editingId) {
      setForm((prev) => ({ ...prev, message: defaultMessage || prev.message }));
    }
  }, [open, defaultMessage, editingId]);

  const resetForm = () => {
    setEditingId(null);
    setCcInput('');
    setForm(defaultFormState);
    setExistingFiles([]);
    setNewFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startAddNew = () => {
    resetForm();
    setActiveTab('form');
    setOpen(true);
  };

  const startEdit = (reminder) => {
    setEditingId(reminder._id);
    setCcInput('');
    setForm({
      message: reminder.message || '',
      emailSubject: reminder.emailSubject || 'Reminder from BooksComply',
      scheduledAt: toDatetimeLocalValue(reminder.scheduledAt),
      reminderType: reminder.reminderType || 'manual',
      repeatType: reminder.repeatType || 'none',
      sendEmail: reminder.sendEmail !== false,
      postToChat: reminder.postToChat !== false,
      ccEmails: Array.isArray(reminder.ccEmails) ? reminder.ccEmails : [],
    });
    setExistingFiles(Array.isArray(reminder.files) ? reminder.files : []);
    setNewFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setActiveTab('form');
    setOpen(true);
  };

  const addCcEmail = () => {
    const email = ccInput.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_PATTERN.test(email)) {
      alert('Please enter a valid CC email address.');
      return;
    }
    if (form.ccEmails.includes(email)) {
      alert('This CC email is already added.');
      return;
    }
    setForm((prev) => ({ ...prev, ccEmails: [...prev.ccEmails, email] }));
    setCcInput('');
  };

  const removeCcEmail = (email) => {
    setForm((prev) => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((item) => item !== email),
    }));
  };

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCcEmail();
    }
  };

  const collectCcEmails = () => {
    let ccEmails = [...form.ccEmails];
    const pendingCc = ccInput.trim().toLowerCase();
    if (pendingCc) {
      if (!EMAIL_PATTERN.test(pendingCc)) {
        alert('Please enter a valid CC email address.');
        return null;
      }
      if (!ccEmails.includes(pendingCc)) {
        ccEmails.push(pendingCc);
      }
    }
    return ccEmails;
  };

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const remaining = MAX_REMINDER_FILES - existingFiles.length - newFiles.length;
    if (remaining <= 0) {
      alert(`You can attach up to ${MAX_REMINDER_FILES} documents.`);
      e.target.value = '';
      return;
    }
    setNewFiles((prev) => [...prev, ...picked.slice(0, remaining)]);
    e.target.value = '';
  };

  const removeExistingFile = (fileUrl) => {
    setExistingFiles((prev) => prev.filter((file) => file.fileUrl !== fileUrl));
  };

  const removeNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || !form.scheduledAt) return;

    if (!editingId && pending.length >= MAX_PENDING_REMINDERS) {
      alert(`Maximum limit of ${MAX_PENDING_REMINDERS} scheduled reminders reached for this client. Please edit or cancel an existing reminder.`);
      return;
    }

    const ccEmails = collectCcEmails();
    if (ccEmails === null) return;

    const scheduledAt = toIsoFromDatetimeLocal(form.scheduledAt);
    if (new Date(scheduledAt) <= new Date()) {
      alert('Please choose a future date and time. Use "Send now" on a pending reminder to send immediately.');
      return;
    }

    setLoading(true);
    try {
      const isEditing = Boolean(editingId);
      const formData = new FormData();
      if (!isEditing) formData.append('userId', userId);
      formData.append('message', form.message.trim());
      formData.append('emailSubject', form.emailSubject || 'Reminder from BooksComply');
      formData.append('scheduledAt', scheduledAt);
      formData.append('reminderType', form.reminderType);
      formData.append('repeatType', form.repeatType);
      formData.append('sendEmail', String(form.sendEmail));
      formData.append('postToChat', String(form.postToChat));
      formData.append('ccEmails', JSON.stringify(ccEmails));
      if (isEditing) {
        formData.append('keepFiles', JSON.stringify(existingFiles));
      }
      newFiles.forEach((file) => formData.append('files', file));

      const url = isEditing
        ? `${API_BASE}/api/chat/reminders/${editingId}`
        : `${API_BASE}/api/chat/reminders`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem(tokenKey)}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || (isEditing ? 'Failed to update reminder' : 'Failed to schedule reminder'));
      }
      resetForm();
      await loadReminders();
      onScheduled?.();
      setActiveTab('list');
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelReminder = async (id) => {
    if (!window.confirm('Cancel this scheduled reminder?')) return;
    await fetch(`${API_BASE}/api/chat/reminders/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` },
    });
    if (editingId === id) {
      resetForm();
    }
    loadReminders();
  };

  const sendNow = async (id) => {
    if (!window.confirm('Send this reminder email now?')) return;
    setSendingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/chat/reminders/${id}/send-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Failed to send reminder now');
      }
      if (editingId === id) {
        resetForm();
      }
      await loadReminders();
      alert('Reminder email sent successfully.');
    } catch (err) {
      alert(err.message);
      await loadReminders();
    } finally {
      setSendingId(null);
    }
  };

  const pending = reminders.filter((r) => r.status === 'pending' || r.status === 'sending');
  const totalFiles = existingFiles.length + newFiles.length;
  const isLimitReached = !editingId && pending.length >= MAX_PENDING_REMINDERS;

  return (
    <div className="border-b border-gray-200 dark:border-dark-700 bg-gray-50/90 dark:bg-dark-900/90 shrink-0">
      {/* Header Bar - Always Compact Header */}
      <div className="flex items-center justify-between px-3 py-1.5 gap-2 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700">
        <button
          type="button"
          onClick={() => {
            if (open) {
              setOpen(false);
              resetForm();
            } else {
              setOpen(true);
              if (pending.length > 0 && !editingId) setActiveTab('list');
              else setActiveTab('form');
            }
          }}
          className="flex items-center gap-2 text-xs font-semibold text-primary-600 hover:text-primary-700"
        >
          <FaBell className="w-3.5 h-3.5 text-amber-500" />
          <span>Schedule email reminder</span>
          {pending.length > 0 ? (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-[10px] font-bold">
              {pending.length} Active ({pending.length}/5)
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400 text-[10px]">
              0/5
            </span>
          )}
        </button>

        <div className="flex items-center gap-1.5">
          {!open && pending.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setOpen(true);
                setActiveTab('list');
              }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 bg-gray-100 dark:bg-dark-700 px-2 py-0.5 rounded border border-gray-200 dark:border-dark-600"
            >
              <FaEye className="w-3 h-3 text-primary-500" />
              View ({pending.length})
            </button>
          )}

          {!open && pending.length < MAX_PENDING_REMINDERS && (
            <button
              type="button"
              onClick={startAddNew}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded border border-primary-200 dark:border-primary-800"
            >
              <FaPlus className="w-2.5 h-2.5" />
              + Add
            </button>
          )}

          {open ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 dark:text-red-400 hover:text-red-700 bg-red-50 dark:bg-red-900/30 px-2.5 py-0.5 rounded-md border border-red-200 dark:border-red-800 shadow-sm"
              title="Hide reminder panel to see full chat"
            >
              <FaEyeSlash className="w-3 h-3" />
              Hide Panel
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setOpen(true);
                if (pending.length > 0) setActiveTab('list');
                else setActiveTab('form');
              }}
              className="p-1 text-gray-500 hover:text-primary-600"
              title="Expand reminder panel"
            >
              <FaChevronDown className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded View Container (Controlled strictly by `open` state, max height 210px) */}
      {open && (
        <div className="bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
          {/* Sub-header Navigation Tabs */}
          <div className="flex items-center justify-between px-3 py-1 bg-gray-100/70 dark:bg-dark-800/80 border-b border-gray-200 dark:border-dark-700 text-[11px]">
            <div className="flex items-center gap-1">
              {pending.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveTab('list')}
                  className={`px-2.5 py-0.5 rounded-md font-semibold transition-colors ${
                    activeTab === 'list'
                      ? 'bg-white dark:bg-dark-700 text-primary-600 dark:text-primary-300 shadow-sm border border-gray-200 dark:border-dark-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                  }`}
                >
                  Scheduled List ({pending.length})
                </button>
              )}
              {pending.length < MAX_PENDING_REMINDERS && (
                <button
                  type="button"
                  onClick={startAddNew}
                  className={`px-2.5 py-0.5 rounded-md font-semibold transition-colors ${
                    activeTab === 'form' && !editingId
                      ? 'bg-white dark:bg-dark-700 text-primary-600 dark:text-primary-300 shadow-sm border border-gray-200 dark:border-dark-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                  }`}
                >
                  + Add New ({pending.length + 1}/5)
                </button>
              )}
              {editingId && (
                <button
                  type="button"
                  onClick={() => setActiveTab('form')}
                  className={`px-2.5 py-0.5 rounded-md font-semibold transition-colors ${
                    activeTab === 'form'
                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 shadow-sm border border-amber-300'
                      : 'text-amber-600 hover:text-amber-800'
                  }`}
                >
                  Editing Reminder #{pending.findIndex((p) => p._id === editingId) + 1}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              className="text-[10px] text-gray-500 hover:text-red-500 underline"
            >
              Close & return to chat
            </button>
          </div>

          {/* Scrollable Content Box (Max height 210px so chat history stays visible) */}
          <div className="max-h-[210px] overflow-y-auto overscroll-contain p-3 space-y-2">
            {/* FORM VIEW */}
            {activeTab === 'form' && (
              <form onSubmit={handleSubmit} className="space-y-2">
                {editingId ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-1.5">
                    <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
                      Editing Reminder #{pending.findIndex((p) => p._id === editingId) + 1}
                    </p>
                    <button
                      type="button"
                      onClick={startAddNew}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 hover:underline"
                    >
                      <FaPlus className="w-2.5 h-2.5" />
                      Switch to New
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 px-1">
                    <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      Schedule Reminder ({pending.length + 1}/5)
                    </span>
                  </div>
                )}

                {isLimitReached ? (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                    <p className="font-semibold">Maximum 5 scheduled reminders reached (5/5).</p>
                    <p className="mt-1 text-[11px]">
                      To add a new reminder, please edit or cancel an existing reminder from the list below.
                    </p>
                  </div>
                ) : (
                  <>
                    <textarea
                      className="w-full text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-1.5"
                      rows={2}
                      placeholder="Reminder message (sent by email)"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                    />
                    <input
                      className="w-full text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-1.5"
                      placeholder="Email subject"
                      value={form.emailSubject}
                      onChange={(e) => setForm({ ...form, emailSubject: e.target.value })}
                    />
                    <div className="space-y-1">
                      <label className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">CC email (optional)</label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          className="flex-1 text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-1.5"
                          placeholder="admin@example.com"
                          value={ccInput}
                          onChange={(e) => setCcInput(e.target.value)}
                          onKeyDown={handleCcKeyDown}
                        />
                        <button
                          type="button"
                          onClick={addCcEmail}
                          className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-primary-600 hover:bg-primary-50 dark:hover:bg-dark-700"
                          title="Add CC email"
                        >
                          <FaPlus className="w-3 h-3" />
                        </button>
                      </div>
                      {form.ccEmails.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {form.ccEmails.map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-[10px] border border-primary-100 dark:border-primary-800"
                            >
                              CC: {email}
                              <button
                                type="button"
                                onClick={() => removeCcEmail(email)}
                                className="text-primary-500 hover:text-red-500"
                                aria-label={`Remove ${email}`}
                              >
                                <FaTimes className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                          Documents (optional)
                        </label>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={totalFiles >= MAX_REMINDER_FILES}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-[10px] font-semibold text-primary-600 hover:bg-primary-50 dark:hover:bg-dark-700 disabled:opacity-50"
                        >
                          <FaPaperclip className="w-2.5 h-2.5" />
                          Upload
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          multiple
                          onChange={handleFilePick}
                        />
                      </div>
                      {totalFiles > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {existingFiles.map((file) => (
                            <span
                              key={file.fileUrl}
                              className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded bg-slate-100 dark:bg-dark-700 text-[10px] text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-dark-600"
                            >
                              <FaFile className="w-2.5 h-2.5 shrink-0 text-primary-500" />
                              <span className="truncate max-w-[120px]">{file.originalName}</span>
                              <button
                                type="button"
                                onClick={() => removeExistingFile(file.fileUrl)}
                                className="text-slate-400 hover:text-red-500"
                              >
                                <FaTimes className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          ))}
                          {newFiles.map((file, index) => (
                            <span
                              key={`${file.name}-${index}`}
                              className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-[10px] text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800"
                            >
                              <FaFile className="w-2.5 h-2.5 shrink-0" />
                              <span className="truncate max-w-[120px]">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeNewFile(index)}
                                className="hover:text-red-500"
                              >
                                <FaTimes className="w-2.5 h-2.5" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <input
                      type="datetime-local"
                      className="w-full text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-1.5"
                      value={form.scheduledAt}
                      onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                      required
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-2 py-1.5"
                        value={form.reminderType}
                        onChange={(e) => setForm({ ...form, reminderType: e.target.value, repeatType: e.target.value === 'manual' ? 'none' : form.repeatType })}
                      >
                        <option value="manual">One-time (manual)</option>
                        <option value="automatic">Recurring (automatic)</option>
                      </select>
                      <select
                        className="text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-2 py-1.5"
                        value={form.repeatType}
                        disabled={form.reminderType === 'manual'}
                        onChange={(e) => setForm({ ...form, repeatType: e.target.value })}
                      >
                        <option value="none">No repeat</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px]">
                      <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} />
                        Send email
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={form.postToChat} onChange={(e) => setForm({ ...form, postToChat: e.target.checked })} />
                        Also post in chat
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50 shadow-sm"
                    >
                      <FaPaperPlane className="w-3 h-3" />
                      {loading
                        ? (editingId ? 'Updating...' : 'Scheduling...')
                        : (editingId ? 'Update reminder' : `Schedule Reminder (${pending.length + 1}/5)`)}
                    </button>
                  </>
                )}
              </form>
            )}

            {/* LIST VIEW */}
            {(activeTab === 'list' || activeTab === 'all') && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <span>Scheduled Reminders ({pending.length}/5)</span>
                  {pending.length < MAX_PENDING_REMINDERS && (
                    <button
                      type="button"
                      onClick={startAddNew}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary-600 hover:text-primary-700 lowercase capitalize"
                    >
                      <FaPlus className="w-2.5 h-2.5" />
                      + Add new
                    </button>
                  )}
                </div>
                {pending.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">No scheduled reminders active.</p>
                ) : (
                  pending.map((r, idx) => (
                    <div
                      key={r._id}
                      className={`flex items-start justify-between gap-2 text-[11px] rounded-lg px-2.5 py-1.5 border transition-colors ${
                        editingId === r._id
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                          : 'bg-white dark:bg-dark-800 border-gray-200 dark:border-dark-700 hover:border-primary-300 dark:hover:border-dark-600'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => startEdit(r)}
                        className="min-w-0 flex-1 text-left hover:opacity-90"
                        title="Click to edit reminder"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-[10px] font-bold">
                            #{idx + 1}
                          </span>
                          <FaClock className="w-3 h-3 text-amber-500 shrink-0" />
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {new Date(r.scheduledAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 pl-6 text-gray-600 dark:text-gray-300 line-clamp-1 text-[11px]">
                          {r.message}
                        </div>
                        <div className="mt-0.5 pl-6 flex flex-wrap items-center gap-2 text-[10px]">
                          <span className="font-semibold text-primary-600 dark:text-primary-400">
                            {getWaitLabel(r.scheduledAt)}
                          </span>
                          {r.emailSubject && (
                            <span className="text-gray-400 truncate max-w-[160px]">
                              Subject: {r.emailSubject}
                            </span>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                        <button
                          type="button"
                          onClick={() => sendNow(r._id)}
                          disabled={sendingId === r._id}
                          className="p-1 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                          title="Send now"
                        >
                          <FaBolt className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => startEdit(r)}
                          className="p-1 rounded text-primary-600 hover:bg-primary-50 dark:hover:bg-dark-700"
                          title="Edit reminder"
                        >
                          <FaEdit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelReminder(r._id)}
                          className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Cancel reminder"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatReminderPanel;
